import { useMutation } from "@apollo/client";
import cn from "classnames";
import { Formik } from "formik";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { MODIFICATION_INFO } from "@api/mutations/refund";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { useStore } from "@hooks/useZustandProvider";
import Button from "@ui-kit/Button/Button";
import Editor from "@ui-kit/cms/Editor/Editor";
import Container from "@ui-kit/Container/Container";
import TicketsInput from "@ui-kit/TicketsInput/TicketsInput";
import { regExpForEmail } from "@utils/utils";

import s from "./ManageBookingBlock.module.scss";

const ManageBookingBlock = ({ settings, updateField, data }) => {
  const { t } = useTranslation(["common"]);
  const { locale, ...router } = useRouter();
  const { title, text1, text2 } = data;
  const { blockType } = settings.common;
  const { user, resetRefund, updateRefund, updateChangeTickets } = useStore();
  const { email = "" } = user;
  const [modificationInfo, { loading }] = useMutation(MODIFICATION_INFO);

  useEffect(() => {
    resetRefund();
  }, []);

  return (
    <Container className={s.manageBooking}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          <Formik
            validateOnMount
            enableReinitialize
            initialValues={{
              email,
              ticketNumber: "",
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email(
                  t("validation.error.email", {
                    field: t("email"),
                  }),
                )
                .matches(
                  regExpForEmail,
                  t("validation.error.email", {
                    field: t("email"),
                  }),
                )
                .required(
                  t("validation.error.require", {
                    field: t("email"),
                  }),
                ),
              ticketNumber: Yup.string().required(
                t("validation.error.require", {
                  field: t("ticket number"),
                }),
              ),
            })}
          >
            {(props) => {
              const {
                values,
                touched,
                errors,
                handleChange,
                handleBlur,
                setFieldError,
              } = props;
              return (
                <form className={s.form}>
                  {/* <h1 className={s.title}>{t("ticket change and return")}</h1> */}
                  <div className={s.content}>
                    <div className={s.description}>
                      <Editor field={{ text1 }} onChange={updateField} />
                    </div>
                    <div className={s.input}>
                      <TicketsInput
                        autoFocus
                        name="email"
                        label={t("email")}
                        type="text"
                        required
                        value={values.email}
                        onChange={(e) => {
                          e.target.value = e.target.value.trim();
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        errors={touched.email && errors.email}
                      />
                    </div>
                    <div className={s.input}>
                      <TicketsInput
                        autoFocus
                        name="ticketNumber"
                        label={t("ticket number")}
                        placeholder={t("ticket number")}
                        type="text"
                        required
                        value={values.ticketNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={touched.ticketNumber && errors.ticketNumber}
                      />
                    </div>
                    <Button
                      disabled={loading || errors.email || errors.ticketNumber}
                      className={s.button}
                      text={loading ? t("loading") : t("submit")}
                      onClick={() => {
                        modificationInfo({
                          variables: {
                            Email: values.email,
                            TicketNumber: values.ticketNumber,
                            lang: locale,
                          },
                        })
                          .then((data) => {
                            const { error } = data.data.modificationInfo;
                            if (error) {
                              if (
                                error.ErrorCode === "ticket_not_found" ||
                                error.ErrorCode === "invalid_email"
                              ) {
                                toast.error(
                                  t("manage.booking.form.error.notice"),
                                  {
                                    autoClose: 20000,
                                  },
                                );
                              } else {
                                toast.error(error.ErrorReason);
                              }
                            } else {
                              updateRefund({
                                email: values.email,
                                ticketNumber: values.ticketNumber,
                                modificationInfo: data.data.modificationInfo,
                              });

                              updateChangeTickets({
                                email: values.email,
                              });

                              router.push("/manage-booking/trips");
                            }
                          })
                          .catch(() => {
                            toast.error("Something wrong!");
                          });
                      }}
                    />
                    <div className={s.rules}>
                      <Editor field={{ text2 }} onChange={updateField} />
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        </section>
      </Container>
    </Container>
  );
};

export default ManageBookingBlock;
